import type { Node, Edge } from '@xyflow/react'

/**
 * Configuration for subflow/group behavior
 * Following overflow.dev patterns for grouping
 */
export interface SubflowConfig {
  /** Padding around children bounds */
  padding: number
  /** Minimum group width */
  minWidth: number
  /** Minimum group height */
  minHeight: number
  /** Header height for title area */
  headerHeight: number
  /** Animation duration in ms */
  animationDuration: number
  /** Whether to animate bounds changes */
  animateBoundsChanges: boolean
}

export const DEFAULT_SUBFLOW_CONFIG: SubflowConfig = {
  padding: 20,
  minWidth: 320,
  minHeight: 240,
  headerHeight: 40,
  animationDuration: 300,
  animateBoundsChanges: true,
}

/**
 * Ordena los nodos para que los padres aparezcan antes que sus hijos
 * CRÍTICO: React Flow requiere este orden para procesar correctamente los parentId
 */
export function sortNodesByHierarchy<T extends Node>(nodes: T[]): T[] {
  const parentNodes: T[] = []
  const childNodes: T[] = []

  nodes.forEach((node) => {
    if (node.parentId) {
      childNodes.push(node)
    } else {
      parentNodes.push(node)
    }
  })

  return [...parentNodes, ...childNodes]
}

/**
 * Obtiene todos los nodos hijos de un nodo padre
 */
export function getChildNodes<T extends Node>(nodes: T[], parentId: string): T[] {
  return nodes.filter((node) => node.parentId === parentId)
}

/**
 * Obtiene todos los descendientes de un nodo (hijos, nietos, etc.)
 */
export function getDescendantNodes<T extends Node>(nodes: T[], parentId: string): T[] {
  const children = getChildNodes(nodes, parentId)
  const descendants = [...children]

  children.forEach((child) => {
    descendants.push(...getDescendantNodes(nodes, child.id))
  })

  return descendants
}

/**
 * Devuelve la cadena de ancestros (padres, abuelos, ...) de un nodo dada su id
 * El primer elemento es el padre directo y el último es el ancestro raíz
 */
export function getAncestorChain<T extends Node>(nodes: T[], nodeId: string): T[] {
  const chain: T[] = []
  let current = nodes.find((n) => n.id === nodeId) as T | undefined
  while (current?.parentId) {
    const parent = nodes.find((n) => n.id === current!.parentId) as T | undefined
    if (!parent) break
    chain.push(parent)
    current = parent
  }
  return chain
}

/**
 * Calcula la posición absoluta de un nodo sumando las posiciones de todos sus ancestros
 */
export function getAbsolutePositionOfNode<T extends Node>(nodes: T[], node: T): { x: number; y: number } {
  const chain = getAncestorChain(nodes, node.id)
  return chain.reduce(
    (acc, ancestor) => ({ x: acc.x + ancestor.position.x, y: acc.y + ancestor.position.y }),
    { x: node.position.x, y: node.position.y }
  )
}

/**
 * Convierte coordenadas relativas (respecto a un parentId) a absolutas considerando toda la cadena de ancestros
 */
export function relativeToAbsolutePositionDeep(
  relativePos: { x: number; y: number },
  parentId: string,
  nodes: Node[]
): { x: number; y: number } {
  const chain = getAncestorChain(nodes, parentId)
  const parent = nodes.find((n) => n.id === parentId)
  const sum = [...(parent ? [parent] : []), ...chain].reduce(
    (acc, ancestor) => ({ x: acc.x + ancestor.position.x, y: acc.y + ancestor.position.y }),
    { x: relativePos.x, y: relativePos.y }
  )
  return sum
}

/**
 * Convierte coordenadas absolutas a relativas respecto de un grupo objetivo considerando su cadena de ancestros
 */
export function absoluteToRelativePositionDeep(
  absolutePos: { x: number; y: number },
  targetParentId: string,
  nodes: Node[]
): { x: number; y: number } {
  let offset = { x: 0, y: 0 }
  const target = nodes.find((n) => n.id === targetParentId)
  if (target) {
    const chain = getAncestorChain(nodes, target.id)
    offset = [...chain, target].reduce(
      (acc, ancestor) => ({ x: acc.x + ancestor.position.x, y: acc.y + ancestor.position.y }),
      { x: 0, y: 0 }
    )
  }
  return { x: absolutePos.x - offset.x, y: absolutePos.y - offset.y }
}

/**
 * Calcula el bounding box que contiene todos los nodos hijos
 * Útil para auto-redimensionar grupos
 */
export function calculateChildrenBounds(childNodes: Node[]): {
  width: number
  height: number
  minX: number
  minY: number
  maxX: number
  maxY: number
} {
  if (childNodes.length === 0) {
    return { width: 200, height: 150, minX: 0, minY: 0, maxX: 200, maxY: 150 }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  childNodes.forEach((node) => {
    const nodeWidth = node.width || node.style?.width || 150
    const nodeHeight = node.height || node.style?.height || 40

    minX = Math.min(minX, node.position.x)
    minY = Math.min(minY, node.position.y)
    maxX = Math.max(maxX, node.position.x + (typeof nodeWidth === 'number' ? nodeWidth : 150))
    maxY = Math.max(maxY, node.position.y + (typeof nodeHeight === 'number' ? nodeHeight : 40))
  })

  return {
    width: maxX - minX,
    height: maxY - minY,
    minX,
    minY,
    maxX,
    maxY,
  }
}

/**
 * Convierte coordenadas absolutas a relativas al padre
 */
export function absoluteToRelativePosition(
  absolutePos: { x: number; y: number },
  parentPos: { x: number; y: number }
): { x: number; y: number } {
  return {
    x: absolutePos.x - parentPos.x,
    y: absolutePos.y - parentPos.y,
  }
}

/**
 * Convierte coordenadas relativas al padre a absolutas
 */
export function relativeToAbsolutePosition(
  relativePos: { x: number; y: number },
  parentPos: { x: number; y: number }
): { x: number; y: number } {
  return {
    x: relativePos.x + parentPos.x,
    y: relativePos.y + parentPos.y,
  }
}

/**
 * Verifica si un punto está dentro de un nodo
 */
export function isPointInsideNode(
  point: { x: number; y: number },
  node: Node,
  nodes?: Node[]
): boolean {
  const nodeWidth = node.width || node.style?.width || 150
  const nodeHeight = node.height || node.style?.height || 40
  const width = typeof nodeWidth === 'number' ? nodeWidth : 150
  const height = typeof nodeHeight === 'number' ? nodeHeight : 40
  // Usar posición absoluta si se provee el listado de nodos (para manejar anidación)
  let pos = node.position
  if (nodes) {
    const abs = getAbsolutePositionOfNode(nodes, node)
    pos = abs
  }
  return (
    point.x >= pos.x &&
    point.x <= pos.x + width &&
    point.y >= pos.y &&
    point.y <= pos.y + height
  )
}

/**
 * Encuentra el nodo grupo en el que se encuentra un punto
 */
export function findGroupAtPoint(
  point: { x: number; y: number },
  nodes: Node[],
  excludeNodeId?: string
): Node | null {
  // Buscar de atrás hacia adelante para obtener el grupo más arriba en el z-index
  for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i]
    if (
      node.id !== excludeNodeId &&
      node.type === 'subflow' &&
      !node.hidden &&
      node.data?.collapsed !== true &&
      isPointInsideNode(point, node, nodes)
    ) {
      return node
    }
  }
  return null
}

/**
 * Oculta/muestra un grupo y todos sus descendientes
 */
export function toggleGroupVisibility<T extends Node>(
  nodes: T[],
  groupId: string,
  hidden: boolean
): T[] {
  const descendants = getDescendantNodes(nodes, groupId)
  const descendantIds = new Set(descendants.map((n) => n.id))

  return nodes.map((node) => {
    if (descendantIds.has(node.id)) {
      return { ...node, hidden }
    }
    return node
  })
}

/**
 * Oculta las edges conectadas a nodos ocultos
 */
export function hideEdgesForHiddenNodes(edges: Edge[], nodes: Node[]): Edge[] {
  const hiddenNodeIds = new Set(nodes.filter((n) => n.hidden).map((n) => n.id))

  return edges.map((edge) => {
    const shouldHide =
      hiddenNodeIds.has(edge.source) || hiddenNodeIds.has(edge.target)
    if (edge.hidden !== shouldHide) {
      return { ...edge, hidden: shouldHide }
    }
    return edge
  })
}

/**
 * Verifica si una conexión es válida según las reglas de sub-flows
 */
export function isValidSubFlowConnection(
  connection: { source: string; target: string },
  nodes: Node[]
): boolean {
  const sourceNode = nodes.find((n) => n.id === connection.source)
  const targetNode = nodes.find((n) => n.id === connection.target)

  if (!sourceNode || !targetNode) return false

  // Evitar que un nodo se conecte a su padre directo
  if (sourceNode.parentId === connection.target) return false
  if (targetNode.parentId === connection.source) return false

  // Evitar que un padre se conecte a su hijo directo
  if (sourceNode.id === targetNode.parentId) return false
  if (targetNode.id === sourceNode.parentId) return false

  // Evitar conexiones entre ancestro y descendiente (en cualquier nivel)
  const sourceAncestors = new Set(getAncestorChain(nodes, sourceNode.id).map((n) => n.id))
  const targetAncestors = new Set(getAncestorChain(nodes, targetNode.id).map((n) => n.id))
  if (sourceAncestors.has(targetNode.id)) return false // target es ancestro de source
  if (targetAncestors.has(sourceNode.id)) return false // source es ancestro de target

  return true
}

/**
 * Agrupa nodos seleccionados en un nuevo grupo
 *
 * IMPORTANTE: Esta función asume que selectedNodeIds ya ha sido filtrado
 * para excluir StickyNotes. Los StickyNote NO deben ser parte de grupos.
 * La validación debe hacerse en useSubFlows.createGroup antes de llamar esta función.
 */
export function groupSelectedNodes<T extends Node>(
  nodes: T[],
  selectedNodeIds: string[],
  groupId: string,
  groupLabel: string
): T[] {
  const selectedNodes = nodes.filter((n) => selectedNodeIds.includes(n.id))

  if (selectedNodes.length === 0) return nodes

  // Calcular el bounding box de los nodos seleccionados
  const bounds = calculateChildrenBounds(selectedNodes)
  const padding = 20

  // Crear el nodo grupo
  const groupNode: Node = {
    id: groupId,
    type: 'subflow',
    position: { x: bounds.minX - padding, y: bounds.minY - padding },
    data: { label: groupLabel, collapsed: false },
    style: {
      width: bounds.width + padding * 2,
      height: bounds.height + padding * 2,
    },
  }

  // Actualizar los nodos seleccionados para que sean hijos del grupo
  const updatedNodes = nodes.map((node) => {
    if (selectedNodeIds.includes(node.id)) {
      return {
        ...node,
        parentId: groupId,
        position: absoluteToRelativePosition(
          node.position,
          { x: bounds.minX - padding, y: bounds.minY - padding }
        ),
        extent: 'parent' as const,
      }
    }
    return node
  })

  // Agregar el grupo y reordenar
  return sortNodesByHierarchy([groupNode as T, ...updatedNodes])
}

/**
 * Desagrupa nodos, convirtiéndolos de hijos a nodos raíz
 */
export function ungroupNodes<T extends Node>(
  nodes: T[],
  groupId: string
): T[] {
  const groupNode = nodes.find((n) => n.id === groupId)
  if (!groupNode) return nodes

  const childNodes = getChildNodes(nodes, groupId)

  // Convertir posiciones relativas a absolutas
  const updatedNodes = nodes
    .filter((n) => n.id !== groupId) // Remover el grupo
    .map((node) => {
      if (childNodes.some((child) => child.id === node.id)) {
        return {
          ...node,
          parentId: undefined,
          position: relativeToAbsolutePositionDeep(node.position, groupNode.id, nodes),
          extent: undefined,
        }
      }
      return node
    })

  return sortNodesByHierarchy(updatedNodes)
}

// ============================================
// ENHANCED FUNCTIONS FOR OVERFLOW.DEV-STYLE GROUPING
// ============================================

/**
 * Calculate optimal group bounds with configurable settings
 * Follows overflow.dev pattern for AABB calculation
 */
export function calculateOptimalGroupBounds(
  childNodes: Node[],
  config: SubflowConfig = DEFAULT_SUBFLOW_CONFIG
): {
  x: number
  y: number
  width: number
  height: number
  childrenBounds: { minX: number; minY: number; maxX: number; maxY: number }
} {
  const { padding, minWidth, minHeight, headerHeight } = config

  if (childNodes.length === 0) {
    return {
      x: 0,
      y: 0,
      width: minWidth,
      height: minHeight,
      childrenBounds: { minX: padding, minY: padding + headerHeight, maxX: minWidth - padding, maxY: minHeight - padding }
    }
  }

  // Calculate AABB (Axis-Aligned Bounding Box) from children
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  childNodes.forEach((node) => {
    // Get actual node dimensions (measured or fallback)
    const nodeWidth = node.measured?.width ?? node.width ?? (node.style?.width as number) ?? 150
    const nodeHeight = node.measured?.height ?? node.height ?? (node.style?.height as number) ?? 40

    const nodeRight = node.position.x + nodeWidth
    const nodeBottom = node.position.y + nodeHeight

    minX = Math.min(minX, node.position.x)
    minY = Math.min(minY, node.position.y)
    maxX = Math.max(maxX, nodeRight)
    maxY = Math.max(maxY, nodeBottom)
  })

  // Apply padding and minimum size constraints
  const contentWidth = maxX - minX
  const contentHeight = maxY - minY
  const width = Math.max(contentWidth + padding * 2, minWidth)
  const height = Math.max(contentHeight + padding * 2 + headerHeight, minHeight)

  return {
    x: minX - padding,
    y: minY - padding - headerHeight,
    width,
    height,
    childrenBounds: { minX, minY, maxX, maxY }
  }
}

/**
 * Check if a node's center is inside another node's bounds
 * Used for drag-drop target detection
 */
export function isNodeCenterInsideBounds(
  draggedNode: Node,
  targetNode: Node,
  nodes: Node[]
): boolean {
  // Get dragged node dimensions
  const dragWidth = draggedNode.measured?.width ?? draggedNode.width ?? 150
  const dragHeight = draggedNode.measured?.height ?? draggedNode.height ?? 40

  // Calculate center of dragged node (in absolute coordinates)
  const dragAbsPos = draggedNode.parentId
    ? relativeToAbsolutePositionDeep(draggedNode.position, draggedNode.parentId, nodes)
    : draggedNode.position

  const centerX = dragAbsPos.x + (dragWidth as number) / 2
  const centerY = dragAbsPos.y + (dragHeight as number) / 2

  // Get target node bounds (in absolute coordinates)
  const targetAbsPos = targetNode.parentId
    ? relativeToAbsolutePositionDeep(targetNode.position, targetNode.parentId, nodes)
    : targetNode.position

  const targetWidth = targetNode.measured?.width ?? targetNode.width ?? (targetNode.style?.width as number) ?? 200
  const targetHeight = targetNode.measured?.height ?? targetNode.height ?? (targetNode.style?.height as number) ?? 150

  return (
    centerX >= targetAbsPos.x &&
    centerX <= targetAbsPos.x + targetWidth &&
    centerY >= targetAbsPos.y &&
    centerY <= targetAbsPos.y + targetHeight
  )
}

/**
 * Find the best drop target for a dragged node
 * Returns the deepest valid group that contains the node's center
 */
export function findBestDropTarget(
  draggedNode: Node,
  nodes: Node[],
  options: {
    excludeIds?: Set<string>
    maxNestingLevel?: number
  } = {}
): Node | null {
  const { excludeIds = new Set(), maxNestingLevel = 5 } = options

  // Get all subflow nodes that could be targets
  const subflowNodes = nodes.filter(n =>
    n.type === 'subflow' &&
    n.id !== draggedNode.id &&
    !excludeIds.has(n.id) &&
    !n.hidden &&
    n.data?.collapsed !== true
  )

  // Check for circular reference prevention
  const dragDescendants = new Set(getDescendantNodes(nodes, draggedNode.id).map(n => n.id))
  const validTargets = subflowNodes.filter(n => !dragDescendants.has(n.id))

  // Find all targets that contain the dragged node's center
  const containingTargets = validTargets.filter(target =>
    isNodeCenterInsideBounds(draggedNode, target, nodes)
  )

  if (containingTargets.length === 0) {
    return null
  }

  // Sort by nesting level (deepest first) and select the most nested valid target
  const targetsWithLevel = containingTargets.map(target => ({
    node: target,
    level: getAncestorChain(nodes, target.id).length
  }))

  targetsWithLevel.sort((a, b) => b.level - a.level)

  // Find the deepest target that doesn't exceed max nesting
  for (const { node, level } of targetsWithLevel) {
    const dragNodeLevel = draggedNode.parentId
      ? getAncestorChain(nodes, draggedNode.id).length
      : 0
    const targetChildLevel = level + 1

    if (targetChildLevel <= maxNestingLevel) {
      return node
    }
  }

  return null
}

/**
 * Calculate the relative position for a node being added to a group
 * Preserves visual position by converting from absolute to relative coordinates
 */
export function calculateRelativePositionForGroup(
  node: Node,
  targetGroupId: string,
  nodes: Node[],
  config: SubflowConfig = DEFAULT_SUBFLOW_CONFIG
): { x: number; y: number } {
  // Get node's current absolute position
  const absolutePos = node.parentId
    ? relativeToAbsolutePositionDeep(node.position, node.parentId, nodes)
    : node.position

  // Convert to relative position within target group
  return absoluteToRelativePositionDeep(absolutePos, targetGroupId, nodes)
}

/**
 * Recalculate group bounds and update positions atomically
 * This ensures children don't visually move when group resizes
 */
export function recalculateGroupWithChildren<T extends Node>(
  nodes: T[],
  groupId: string,
  config: SubflowConfig = DEFAULT_SUBFLOW_CONFIG
): T[] {
  const groupNode = nodes.find(n => n.id === groupId)
  if (!groupNode || groupNode.type !== 'subflow') return nodes

  const childNodes = getChildNodes(nodes, groupId)
  if (childNodes.length === 0) {
    // Just apply minimum size for empty groups
    return nodes.map(n =>
      n.id === groupId
        ? {
            ...n,
            style: {
              ...n.style,
              width: config.minWidth,
              height: config.minHeight,
            },
          }
        : n
    )
  }

  const { x: newX, y: newY, width, height, childrenBounds } = calculateOptimalGroupBounds(childNodes, config)

  // Calculate the position delta
  const deltaX = newX - groupNode.position.x
  const deltaY = newY - groupNode.position.y

  // Update group position/size and adjust children positions to compensate
  return nodes.map(n => {
    if (n.id === groupId) {
      return {
        ...n,
        position: { x: newX, y: newY },
        style: {
          ...n.style,
          width,
          height,
        },
      }
    }

    // If this is a child of the group, adjust its position to compensate for group movement
    if (n.parentId === groupId) {
      return {
        ...n,
        position: {
          x: n.position.x - deltaX,
          y: n.position.y - deltaY,
        },
      }
    }

    return n
  })
}

/**
 * Get all groups that need to be resized after a node operation
 * Returns groups in order from deepest to shallowest (bottom-up)
 */
export function getGroupsToResize(
  nodeIds: string[],
  nodes: Node[]
): string[] {
  const groupsToResize = new Set<string>()

  nodeIds.forEach(nodeId => {
    const node = nodes.find(n => n.id === nodeId)
    if (!node) return

    // Add the node's parent group
    if (node.parentId) {
      groupsToResize.add(node.parentId)
    }

    // If this is a group, add it too
    if (node.type === 'subflow') {
      groupsToResize.add(node.id)
    }

    // Add all ancestor groups
    getAncestorChain(nodes, nodeId).forEach(ancestor => {
      if (ancestor.type === 'subflow') {
        groupsToResize.add(ancestor.id)
      }
    })
  })

  // Sort by depth (deepest first for proper resize order)
  const groupsArray = Array.from(groupsToResize)
  return groupsArray.sort((a, b) => {
    const depthA = getAncestorChain(nodes, a).length
    const depthB = getAncestorChain(nodes, b).length
    return depthB - depthA
  })
}

/**
 * Check if moving a node to a target would create a circular reference
 */
export function wouldCreateCircularReference(
  nodeId: string,
  targetGroupId: string,
  nodes: Node[]
): boolean {
  // A node cannot become a child of itself
  if (nodeId === targetGroupId) return true

  // Check if target is a descendant of the node being moved
  const descendants = getDescendantNodes(nodes, nodeId)
  return descendants.some(d => d.id === targetGroupId)
}
